import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { pollSubmittedEvent } from '../../../../../../db/polls/pusher-events';
import { PollQuestion } from '../../../../../../db/polls/types';
import { PollSlideTypes } from '../../../../../../db/types';
import { usePusherEvent } from '../../../../../../pusher';
import { PollParams } from '../../../../../../routes/router';
import { getAlphabetFromIndex } from '../../../../../../utils/helper';
import { calculatePercentage } from '../../../../../../utils/math';
import { enumFromStringValue } from '../../../../../../utils/string';
import { useInterval } from '../../../../../../utils/timer';
import { usePollContext } from '../../../../Context';
import { getCanSeeResults } from './helpers';
import { PublishedOption } from './types';

export interface UsePublishedQuestionOptions {
  pollQuestion: PollQuestion;
}

const usePublishedQuestionOptions = ({ pollQuestion }: UsePublishedQuestionOptions) => {
  const { slideType: slideTypeValues } = useParams<PollParams>();
  const slideType = enumFromStringValue(PollSlideTypes, slideTypeValues);

  const { poll, setPoll } = usePollContext();

  const options = pollQuestion?.options ?? [];

  const publishedOptions: PublishedOption[] = useMemo(
    () =>
      options.map((option, index) => {
        const alphabet = getAlphabetFromIndex(index);
        const selectionPercentage = calculatePercentage(option.totalSelections, pollQuestion.totalAttempts);
        return {
          ...option,
          alphabet,
          selectionPercentage: Math.floor(selectionPercentage),
        };
      }),
    [options]
  );

  const [canSeeResults, setCanSeeResults] = useState(false);

  const { stop } = useInterval(() => {
    const canSeeResults = getCanSeeResults(poll);
    if (!canSeeResults) return;

    setCanSeeResults(true);
    stop();
  }, 1000);

  usePusherEvent(
    pollSubmittedEvent,
    useCallback((event) => {
      const submission = event.payload.submission;

      setPoll((prevPoll) => {
        if (!prevPoll) return prevPoll;

        if (prevPoll.id !== event.payload.pollId) return prevPoll;

        const questionId = prevPoll.questions[0];
        if (!questionId) return prevPoll;

        const question = prevPoll.questionsById?.[questionId];
        if (!question) return prevPoll;

        const questionOptions = question.options.map((option) => {
          if (submission[questionId].optionSelected === option.num) {
            return { ...option, totalSelections: option.totalSelections + 1 };
          }
          return option;
        });

        const questionTotalAttempts = question.totalAttempts + 1;

        return {
          ...prevPoll,
          questionsById: {
            ...prevPoll.questionsById!,
            [questionId]: {
              ...prevPoll.questionsById![questionId]!,
              options: questionOptions,
              totalAttempts: questionTotalAttempts,
            },
          },
        };
      });
    }, [])
  );

  return { slideType, publishedOptions, canSeeResults };
};

export default usePublishedQuestionOptions;
